import * as React from 'react';
import { Icon, IconSize, IconType, IIconProps } from '../Icon';
import styled, { ISpruceThemeProps } from 'styled-components';
import { BodyText } from '../Typography';
import { IHasTestData } from '../../test-utilities/IHasTestData';
import { mergeTests } from '../../test-utilities/mergeTests';

interface IStyledNotificationProps {
  mode: NotificationMode;
  type: NotificationType;
}

const typeToIconTypeMap: { [TKey in NotificationType]: IconType } = {
  error: IconType.CloseCircleFill,
  info: IconType.InfoFill,
  warning: IconType.Warning,
  success: IconType.CheckCircleFill,
};

const getNotificationColor = ({ type, theme }: IStyledNotificationProps & ISpruceThemeProps): string => {
  switch (type) {
    case 'error':
      return theme.colors.error;
    case 'info':
      return theme.colors.blue;
    case 'warning':
      return theme.colors.warning;
    case 'success':
      return theme.colors.primary;
  }
};

const NotificationStyled = styled.div<IStyledNotificationProps>`
  ${(props) => props.theme.fonts.mixins.body};
  align-items: center;
  background-color: ${(props) =>
    props.mode === 'default' ? props.theme.colors.defaultContentBackground : getNotificationColor(props)};
  border: solid 1px ${(props) => props.theme.colors.defaultContentBorder};
  color: ${(props) => (props.mode === 'default' ? props.theme.colors.defaultContentText : props.theme.colors.white)};
  display: grid;
  gap: ${(props) => props.theme.spacing.spacing4};
  grid-template-columns: min-content 1fr;
  grid-template-areas: 'icon .';
  padding: ${(props) => props.theme.spacing.spacing4} ${(props) => props.theme.spacing.spacing5};
  position: relative;

  ::before {
    content: '';
    width: 3px;
    height: 100%;
    position: absolute;
    top: 0;
    left: 0;
    background-color: ${getNotificationColor};
  }

  > * {
    grid-column-start: 2;
  }
`;

export type NotificationType = 'error' | 'info' | 'warning' | 'success';

export type NotificationMode = 'default' | 'inverted';

const IconStyled = styled(Icon)<IIconProps & { mode: NotificationMode }>`
  grid-area: icon;
  ${(props) =>
    props.mode === 'inverted'
      ? `color: ${props.theme.colors.white};`
      : props.type === IconType.CloseCircleFill
        ? `color: ${props.theme.colors.error};`
        : props.type === IconType.InfoFill
          ? `color: ${props.theme.colors.blue};`
          : ''}
`;

export interface INotificationProps extends IHasTestData {
  icon?: IconType | null;
  message?: React.ReactNode;
  mode?: NotificationMode | null;
  type: NotificationType;
  children?: React.ReactNode;
}

export const Notification: React.FC<INotificationProps> = ({
  children,
  icon,
  mode,
  message,
  type,
  'data-test-id': dataTestId,
  'data-test-class': dataTestClass,
}) => {
  const computedType = icon || typeToIconTypeMap[type];
  const computedMode = mode || 'default';
  return (
    <NotificationStyled
      mode={computedMode}
      type={type}
      data-test-id={mergeTests(dataTestId)}
      data-test-class={mergeTests(dataTestClass || 'notification', type)}
    >
      <IconStyled
        size={computedMode === 'default' ? IconSize.Small : IconSize.Regular}
        type={computedType}
        mode={computedMode}
      />
      {typeof message == 'string' ? <BodyText>{message}</BodyText> : message}
      {children}
    </NotificationStyled>
  );
};
